import axios from 'axios';
import { getLS, setLS, removeLS } from '../ls';
import setAuthToken from '../../utils/setAuthToken';
import { resetMixpanelUser } from '../../analytics/mixpanel_client';
import { resetPosthogUser } from '../../analytics/posthog_client';

export const UPLOAD_TIMEOUT_MS = 0;

let isRefreshing = false;
let failedQueue: any[] = [];

const API = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 30000,
});

// Attach access token to requests
API.interceptors.request.use(
    (config) => {
        const token = getLS('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Handle token refresh logic
const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const resetAnalyticsUser = () => {
    resetMixpanelUser();
    resetPosthogUser();
};

API.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.url.includes('/auth/refresh')
        ) {
            // console.log('[⚠️ Interceptor] Access token expired. Attempting refresh...');
            originalRequest._retry = true;

            const refreshToken = getLS('refresh_token');
            if (!refreshToken) {
                resetAnalyticsUser();
                removeLS('access_token');
                removeLS('refresh_token');
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            resolve(API(originalRequest));
                        },
                        reject: (err: any) => reject(err),
                    });
                });
            }

            isRefreshing = true;

            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
                    refresh_token: refreshToken,
                });

                const newAccessToken = response.data.data.accessToken;
                setLS('access_token', newAccessToken);
                setAuthToken(newAccessToken); // set default header

                processQueue(null, newAccessToken);

                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return API(originalRequest);
            } catch (err) {
                processQueue(err, null);
                if (error.response?.status === 401) {
                    redirectToLogin();
                } else {
                    // console.error('[⚠️ Interceptor] Token refresh failed:', err);
                }
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

function redirectToLogin() {
    resetAnalyticsUser();
    removeLS('access_token');
    removeLS('refresh_token');
    window.location.href = '/';
}

export default API;
